import React, { useState} from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Heading,
  Text,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  Progress,
  Select,
  useSteps,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useRegisterStore } from "../store";
import RegisterPasswordStep from "./RegisterPasswordStep";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { servicebookingFlowStore } from "../store/ServiceBookingFlowStore";
import DateOfBirthSelect from './DateOfBirthSelect';

    // eslint-disable-next-line
interface CreateUserStepProps {
  isOpen: boolean;
  onClose: () => void;
}

// eslint-disable-next-line
const CreateUserStep : React.FC = () => {
  const [personalDetails, setData] = useRegisterStore((state) => [
    state.personalDetails,
    state.setData,
  ]);
  const [showPasswordStep, setShowPasswordStep] = useState(false);

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      firstName: personalDetails?.firstName || "",
      lastName: personalDetails?.lastName || "",
      dateOfBirth: personalDetails?.dateOfBirth || "",
      gender: personalDetails?.gender || "",
      email: personalDetails?.email || "",
      mobileNumber: personalDetails?.mobileNumber || "",
    },
  });

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('DD/MM/YYYY') : null
    };

    setData({
      personalDetails: formattedData,
    });

    setShowPasswordStep(true);
  };

  

  const steps = [
    { title: "First", description: "User Details" },
    { title: "Second", description: "Create password" },
    { title: "Third", description: "Confirm User" },
  ];

  const { activeStep } = useSteps({
    index: 1, // Start from the first step
    count: steps.length,
  });

  const max = steps.length - 1;
  const progressPercent = (activeStep / max) * 100;

  return (
    <>
      {!showPasswordStep ? (
        <Box>
            <Box position="relative">
              <Stepper size="sm" index={activeStep} gap="20">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator bg="white">
                      <StepStatus complete={<StepIcon />} />
                    </StepIndicator>
                  </Step>
                ))}
              </Stepper>
              <Progress
                value={progressPercent}
                position="absolute"
                height="3px"
                width="full"
                top="10px"
                zIndex={-1}
              />
            </Box>

            <Box flex="1" p={4} mt={5}>
              <Heading as="h2" size="md" noOfLines={1}>
                Enter Personal Details
              </Heading>
              <Text fontSize="md">
                Input the following details to proceed with signin up
              </Text>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.firstName)}>
                  <FormLabel>First Name</FormLabel>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: "First name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter your First Name"
                        borderRadius="30px"
                      />
                    )}
                  />
                  {errors.firstName && (
                    <Text color="red.500" fontSize="sm">
                      {errors.firstName.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.lastName)}>
                  <FormLabel>Last Name</FormLabel>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: "Last name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter your Last Name"
                        borderRadius="30px"
                      />
                    )}
                  />
                  {errors.lastName && (
                    <Text color="red.500" fontSize="sm">
                      {errors.lastName.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.dateOfBirth)}>
                  <FormLabel>Date of Birth</FormLabel>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{ required: "Date of birth is required" }}
                    render={({ field }) => (
                      <DateOfBirthSelect value={field.value} onChange={field.onChange} />
                    )}
                  />
                  {errors.dateOfBirth && (
                    <Text color="red.500" fontSize="sm">
                      There is a problem in your date of birth
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.gender)}>
                  <FormLabel>Gender</FormLabel>
                  <Controller
                    name="gender"
                    control={control}
                    rules={{ required: "Gender is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder="Select Gender"
                        borderRadius="30px"
                        color={'gray'}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Select>
                    )}
                  />
                  {errors.gender && (
                    <Text color="red.500" fontSize="sm">
                      {errors.gender.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.email)}>
                  <FormLabel>Email</FormLabel>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                        message: "Enter a valid email address",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter your Email"
                        borderRadius="30px"
                      />
                    )}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm">
                      {errors.email.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.mobileNumber)}>
                  <FormLabel>Mobile Number</FormLabel>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    rules={{
                      required: "Mobile number is required",
                      // pattern: {
                      //   value: /^[0-9]{10}$/,
                      //   message: "Mobile number must be exactly 10 digits",
                      // },
                      validate: (value) => {
                        const phoneNumber = parsePhoneNumberFromString(value, 'AU'); 
                        return phoneNumber && phoneNumber.isValid()
                            ? true
                            : "Invalid phone number format";
                    },
                    }}
                    render={({ field: { onChange, value, ...rest } }) => (
                      <InputGroup>
                          <Input
                              {...rest}
                              value={value}
                              borderRadius={30}
                              placeholder="Enter your Phone Number"
                              onBlur={(e) => {
                                  const input = e.target.value;
                                  const phoneNumber = parsePhoneNumberFromString(input, 'AU');
                                  if (phoneNumber && phoneNumber.isValid()) {
                                      onChange(phoneNumber.formatInternational());
                                  } else {
                                      onChange(input);
                                  }
                              }}
                              onChange={onChange}
                          />
                          <InputRightElement>
                              <span role="img" aria-label="Australian Flag" style={{ fontSize: '1.5rem' }}>
                                  🇦🇺
                              </span>
                          </InputRightElement>
                      </InputGroup>

                      
                  )}
                  />
                  {errors.mobileNumber && (
                    <Text color="red.500" fontSize="sm">
                      {errors.mobileNumber.message}
                    </Text>
                  )}
                  <Text mt={5}>Please double check this is correct! We will send this number a verification code when you login.</Text>
                </FormControl>
                
                <Button
                  bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
                  color="#fff"
                  mt={5}
                  width="100%"
                  borderRadius="30px"
                  mb="20px"
                  type="submit"
                >
                  Next
                </Button>
              </form>
            </Box>
            </Box>
          ) : (
            <RegisterPasswordStep />
          )}
    </>
  );
};

export default CreateUserStep;
